import { useEffect } from "react";
import api from "../api";
import { useGuestCart } from "../context/GuestCartContext";

export default function GuestCartBridge() {
  const { addGuestItem } = useGuestCart();

  useEffect(() => {
    const isCartButton = (button) => {
      if (!button) return false;

      const text = button.textContent?.toLowerCase() || "";
      const label = (
        button.getAttribute("aria-label") ||
        button.getAttribute("title") ||
        ""
      ).toLowerCase();

      if (
        text.includes("add to cart") ||
        text.includes("add to bag") ||
        label.includes("add to cart") ||
        label.includes("add to bag")
      ) {
        return true;
      }

      // Header/navigation cart buttons are for opening the cart,
      // not adding a product. Do not let the guest bridge intercept them.
      if (
        label.includes("shopping cart") ||
        label.includes("shopping bag") ||
        label === "cart" ||
        label === "bag"
      ) {
        return false;
      }

      // The product-list buttons use the icon-only
      // FaShoppingCart component, so textContent is empty.
      const svg = button.querySelector("svg");

      return svg?.getAttribute("viewBox") === "0 0 576 512";
    };

    const findProductSelect = (button) => {
      let current = button;

      // Search upward so the select can be found even when
      // it is a sibling of the price/cart row.
      for (let level = 0; level < 8 && current; level += 1) {
        const selects = Array.from(
          current.querySelectorAll?.("select") || []
        );

        const selected =
          selects.find((item) => /^\d+$/.test(String(item.value))) ||
          selects[0];

        if (selected) {
          return selected;
        }

        current = current.parentElement;
      }

      return null;
    };

    const onClick = async (event) => {
      const button = event.target.closest("button");

      if (!isCartButton(button)) return;

      const user = JSON.parse(
        localStorage.getItem("user") || "null"
      );

      // Logged-in users continue using the existing cart logic.
      if (user?.access) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (button.dataset.guestAdding === "1") return;

      button.dataset.guestAdding = "1";

      try {
        let productId =
          window.location.pathname.match(
            /\/product\/(\d+)/
          )?.[1] || null;

        const select = findProductSelect(button);

        if (!productId && select?.id) {
          const match = select.id.match(
            /(?:size|products-size)-(\d+)$/
          );

          if (match) {
            productId = match[1];
          }
        }

        let product;

        if (productId) {
          product = (
            await api.get(
              `/api/products/${productId}/`
            )
          ).data;
        } else if (select?.value) {
          // Product cards expose the selected ProductSize ID.
          // Find the matching product without requiring login.
          const all = await api.get("/api/products/");

          const products = Array.isArray(all.data)
            ? all.data
            : all.data?.results || [];

          product = products.find((item) =>
            item.sizes?.some(
              (size) =>
                String(size.id) ===
                String(select.value)
            )
          );
        }

        if (!product) {
          throw new Error(
            "Product could not be identified."
          );
        }

        if (product.is_sold) {
          throw new Error(
            "This product has been sold."
          );
        }

        const sizes = Array.isArray(product.sizes)
          ? product.sizes
          : [];

        let sizeId = select?.value;

        if (!sizeId && sizes.length === 1) {
          sizeId = sizes[0].id;
        }

        if (!sizeId) {
          throw new Error(
            "Please select a size first."
          );
        }

        const size = sizes.find(
          (item) =>
            String(item.id) ===
            String(sizeId)
        );

        if (!size) {
          throw new Error(
            "Please select a valid size."
          );
        }

        // Guest cart is stored in the existing
        // GuestCartContext/localStorage implementation.
        addGuestItem(product, size);

        window.dispatchEvent(
          new CustomEvent("a1GuestToast", {
            detail: "Added to cart",
          })
        );
      } catch (error) {
        console.error(
          "Guest add-to-cart error:",
          error
        );

        window.dispatchEvent(
          new CustomEvent("a1GuestToast", {
            detail:
              error.response?.data?.detail ||
              error.message ||
              "Unable to add to cart.",
          })
        );
      } finally {
        button.dataset.guestAdding = "0";
      }
    };

    document.addEventListener(
      "click",
      onClick,
      true
    );

    return () =>
      document.removeEventListener(
        "click",
        onClick,
        true
      );
  }, [addGuestItem]);

  return null;
}
